/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import {Delete24Regular} from '@fluentui/react-icons';
import {
	Badge,
	Card,
	createTableColumn,
	DataGrid,
	DataGridBody,
	DataGridCell,
	DataGridHeader,
	DataGridHeaderCell,
	DataGridRow,
	makeStyles,
	TableCellLayout,
	TableColumnDefinition,
	Text,
	Menu,
	MenuItem,
	MenuList,
	MenuPopover,
	MenuTrigger,
	tokens,
	SplitButton,
	Avatar,
	Button,
	TableColumnId,
	DataGridCellFocusMode,
	MenuButton,
	Divider,
	Link,
	Tooltip,
} from '@fluentui/react-components';
import AutomationRuleListProvider, { Item, useAutomationRuleListController } from './automation-rule-list-provider';
import ListItemSkeleton from './list-item-skeleton';
import AutomationRuleListFilterContent from './automation-rule-list-filter';
import { useAzureProvider } from 'src/azure/context';
import { useNavigate, Link as RrdLink } from 'react-router-dom';
import EmptyActivity from 'src/components/shared/empty-activity';
import TemplatesDialog, { useTemplateDialogRef } from './templates';
import { findAutomationRuleSettingsService } from 'src/services/appgami-api/find-automation-rule-settings';
import { UpdateAutomationRuleDataType } from 'src/services/appgami-api/update-automation-rules-service';
import { mapActionsOnUpdate, mapConditionsOnUpdate } from './map-values-on-update';


const TableHeaderTitle = (props: { children?: ReactNode; style?: React.CSSProperties | undefined }) => {
	const classes = useStyles();
	return (
		<Text
			as='span'
			className={classes.tableHeaderTitle}
			style={props?.style}
		>
			{props?.children}
		</Text>
	);
};

const RenderStatus = (props: {status: string}) => {
	if (props.status === 'enabled') {
		return <Badge appearance="filled" color="success">Enabled</Badge>;
	}

	return <Badge appearance="tint" color="severe">Disabled</Badge>;
};

const RenderAvatar = (props: { name: string; image: string; mail: string }) => {
	const classes = useStyles();

	return (
		<TableCellLayout>
			<div className={classes.authorCellContent}>
				<Avatar name={props.name} image={{ src: props.image }} />
				<Text className={classes.authorCellText}>{props.name}</Text>
			</div>
		</TableCellLayout>
	);
};

interface RenderDeleteProps {
	itemId: string;
}

const RenderDelete = (props: RenderDeleteProps) => {
	const classes = useStyles();
	const { openDeleteAutomationRuleModalHandler, setJustOneToDelete } = useAutomationRuleListController();

	return (
		<div className={classes.deleteCell}>
			<Button
				appearance="transparent"
				icon={<Delete24Regular />} 
				onClick={(ev) => {
					ev.stopPropagation();
					setJustOneToDelete(props.itemId);
					openDeleteAutomationRuleModalHandler();
				}}
			/>
		</div>
	);
};

const AutomationRuleName = (props: { name: string, id: string }) => {
	return (
		<TableCellLayout>
			<RrdLink to={`/automation-rule?id=${props.id}`}>
				<Link onClick={(ev) => {
					ev.stopPropagation();
				}}>
					{props.name}
				</Link>
			</RrdLink>
		</TableCellLayout>
	);
};

const RenderCreatedAt = (props: { date: Date }) => {
	const classes = useStyles();

	return (
		<Text className={classes.createdAtText}>{new Date(props.date).toLocaleDateString()}</Text>
	);
};

const columns: TableColumnDefinition<Item>[] = [
	createTableColumn<Item>({
		columnId: 'name',
		renderHeaderCell: () => <TableHeaderTitle>Automation rule</TableHeaderTitle>,
		renderCell: (item) => <AutomationRuleName name={item.name.label} id={item.automationRuleItem.id} />
	}),  
	createTableColumn<Item>({
		columnId: 'status',
		renderHeaderCell: () => <TableHeaderTitle>Status</TableHeaderTitle>,
		renderCell: (item) => <TableCellLayout><RenderStatus status={item.status.label} /></TableCellLayout>
	}),
	createTableColumn<Item>({
		columnId: 'author',
		renderHeaderCell: () => <TableHeaderTitle>Author</TableHeaderTitle>,
		renderCell: (item) => <RenderAvatar {...item.author} />
	}),
	createTableColumn<Item>({
		columnId: 'created_at',
		renderHeaderCell: () => (
			<span style={{
				width: '100%',
				display: 'flex',
				justifyContent: 'center'
			}}>
				<TableHeaderTitle>
					Created on
				</TableHeaderTitle>
			</span>
		),
		renderCell: (item) => (
			<RenderCreatedAt date={item.createdAt.date} />
		)
	}),
	createTableColumn<Item>({
		columnId: 'delete',
		renderHeaderCell: () => null,
		renderCell: (item) => (
			<TableCellLayout style={{ display: 'flex', justifyContent: 'flex-end' }}>
				<Tooltip content="Delete" relationship="label">
					<RenderDelete itemId={item.automationRuleItem.id} />
				</Tooltip>
			</TableCellLayout>
		)
	}),
];

const getCellFocusMode = (columnId: TableColumnId): DataGridCellFocusMode => {
	switch (columnId) {
	case 'delete':
		return 'none';
	default:
		return 'cell';
	}
};

function RulesContent() {
	const classes = useStyles();
	const templatesDialogRef = useTemplateDialogRef();

	const {applicationContext} = useAzureProvider();
	const {
		list,
		selectedItems,
		setSelectedItems,
		loadAutomationRuleList,
		loadingAutomationRuleList,
		updateAutomationRules,
		openDeleteAutomationRuleModalHandler
	} = useAutomationRuleListController();

	const navigate = useNavigate();

	const updateStatusFromAutomationRules = useCallback(async (status: string, ids: Array<string>) => {

		const ruleListWithActor = async (id: string): Promise<UpdateAutomationRuleDataType> => {
			const foundAutomations = list.find((item) => item.automationRuleItem.id === id) as Item;

			const settingsResult = await findAutomationRuleSettingsService({
				projectId: applicationContext.projectId,
				organizationName: applicationContext.organizationName,
				automationRuleId: id,
			});

			const data = {
				...foundAutomations.automationRuleItem,
				status: status,
				organizationId: applicationContext.organizationId,
				projectId: applicationContext.projectId,
				organizationName: applicationContext.organizationName,
				trigger: {
					type: foundAutomations.automationRuleItem.trigger.type,
					data: foundAutomations.automationRuleItem.trigger.values,
				},
				conditions: mapConditionsOnUpdate(foundAutomations.automationRuleItem.conditions),
				actions: mapActionsOnUpdate(foundAutomations.automationRuleItem.actions),
			};

			if (settingsResult.type === 'success') {
				return {
					...data,
					actor: settingsResult.data.settings.actor.type as 'user' | 'service-principal',
				};
			}

			return {
				...data,
				actor: 'user' as 'user' | 'service-principal',
			};
		};

		await updateAutomationRules(await Promise.all(ids.map(ruleListWithActor)));
	}, [applicationContext, list]);

	const init = useRef(true);

	const isEmpty = useMemo(() => {
		return (!loadingAutomationRuleList && list.length === 0);
	}, [list, loadingAutomationRuleList]);

	useEffect(() => {
		if (init.current) {
			init.current = false;
			loadAutomationRuleList();
		}
	}, []);

	const setColumnClassName = useCallback((columnId: string) => {
		if (columnId === 'name') {
			return classes.nameCellRoot;
		}

		if (columnId === 'created_at') {
			return classes.createdAtCellRoot;
		}

		if (columnId === 'delete') {
			return classes.deleteCellRoot;
		}

		return undefined;
	}, []);

	return (
		<div className={classes.root}>
			<Card>
				<AutomationRuleListFilterContent />
			</Card>
			<Card className={classes.cardTable}>
				<DataGrid
					items={list}
					columns={columns}
					selectionMode="multiselect"
					size="medium"
					getRowId={(item) => item.automationRuleItem.id}
					selectedItems={selectedItems}
					onSelectionChange={(_, data) => {
						const list = Array.from(data.selectedItems);
						setSelectedItems(list.map(item => item.toString()));
					}}
				>
					{!isEmpty && (
						<DataGridHeader>
							<div className={classes.tableHeaderTop}>
								<span>
									<Text className={classes.totalRules}>Total</Text>
									<Badge color="informative" size="large">{list.length}</Badge>
								</span>
								<div style={{ gap: '16px', display: 'flex', alignItems: 'center' }}>
									{(!selectedItems.length) && (
										<Menu>
											<MenuTrigger disableButtonEnhancement>
												<SplitButton appearance="primary">Create</SplitButton>
											</MenuTrigger>
											<MenuPopover>
												<MenuList>
													<MenuItem
														onClick={() => {
															navigate('/automation-rule');
														}}
													>
														Automation rule
													</MenuItem>
													<MenuItem
														onClick={() => {
															templatesDialogRef.current?.open();
														}}
													>
														Automation rule with a template
													</MenuItem>
												</MenuList>
											</MenuPopover>
										</Menu>
									)}
									{(selectedItems.length > 0) && (
										<Menu positioning="below-end">
											<MenuTrigger disableButtonEnhancement>
												<MenuButton
													appearance='subtle'
													className={classes.deleteAutomationRules}
												>
													Manage ({selectedItems.length})
												</MenuButton>
											</MenuTrigger>
											<MenuPopover>
												<MenuList>
													<MenuItem
														onClick={() => {
															openDeleteAutomationRuleModalHandler();
														}}
													>
														Delete
													</MenuItem>
													<Divider />
													<MenuItem
														onClick={() => {
															updateStatusFromAutomationRules('enabled', selectedItems);
														}}
													>
														Enable
													</MenuItem>
													<MenuItem
														onClick={() => {
															updateStatusFromAutomationRules('disabled', selectedItems);
														}}
													>
														Disable
													</MenuItem>
												</MenuList>
											</MenuPopover>
										</Menu>
									)}
								</div>
							</div>
							<DataGridRow className={classes.tableHeaderRow}>
								{({ renderHeaderCell, columnId }) => (
									<DataGridHeaderCell
										className={setColumnClassName(columnId as string)}
									>
										{renderHeaderCell()}
									</DataGridHeaderCell>
								)}
							</DataGridRow>
						</DataGridHeader>
					)}
					{
						loadingAutomationRuleList && (
							<>
								<ListItemSkeleton />
								<ListItemSkeleton />
								<ListItemSkeleton />
							</>
						)
					}
					{isEmpty && (
						<>
							<EmptyActivity
								title="No automation rules"
								description="Here you will store all automation rules created for this project"
							/>
							<div className={classes.createAutomationRuleButtonForEmptyState}>
								<Menu>
									<MenuTrigger disableButtonEnhancement>
										<SplitButton appearance="primary">Create automation rule</SplitButton>
									</MenuTrigger>
									<MenuPopover>
										<MenuList>
											<MenuItem
												onClick={() => {
													navigate('/automation-rule');
												}}
											>
												Automation rule
											</MenuItem>
											<MenuItem
												onClick={() => {
													templatesDialogRef.current?.open();
												}}
											>
												Automation rule with a template
											</MenuItem>
										</MenuList>
									</MenuPopover>
								</Menu>
							</div>
						</>
					)}
					{
						!loadingAutomationRuleList && (
							<DataGridBody<Item>>
								{({ item, rowId }) => (
									<DataGridRow key={rowId} className={classes.tableRow}>
										{({renderCell, columnId}) => (
											<DataGridCell
												focusMode={getCellFocusMode(columnId)}
												className={setColumnClassName(columnId as string)}
											>
												{renderCell(item)}
											</DataGridCell>
										)}
									</DataGridRow>
								)}
							</DataGridBody>
						)
					}
				</DataGrid>
			</Card>
			<>
				<TemplatesDialog ref={templatesDialogRef} />
			</>
		</div>
	);
}

export default function Rules() {
	return (
		<AutomationRuleListProvider>
			<RulesContent />
		</AutomationRuleListProvider>
	);
}

const useStyles = makeStyles({
	root: {
		padding: '16px 0',
		boxSizing: 'border-box'
	},
	filterRoot: {
		display: 'flex',
		justifyContent: 'space-between',
		gap: '16px'
	},
	filterInput: {
		width: '100%'
	},
	filterDropdownWrapper: {
		display: 'flex',
		gap: '16px'
	},
	deleteAutomationRules: {
		backgroundColor: tokens.colorNeutralBackground3,
		':hover': {
			backgroundColor: tokens.colorNeutralBackground3Hover
		},
		// '& button:hover + button, button:hover': {
		// 	backgroundColor: tokens.colorNeutralBackground2Hover
		// },
	},
	dropdown: {
		minWidth: '120px',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralShadowAmbientLighter
		},
	},
	cardTable: {
		padding: '0px',
		margin: '16px 0'
	},
	tableHeaderTop: {
		padding: '24px 24px',
		boxSizing: 'border-box',
		display: 'flex',
		justifyContent: 'space-between',
		height: '80px'
	},
	totalRules: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase300,
		marginRight: '8px'
	},
	tableHeaderRow: {
		padding: '6px 8px',
		boxSizing: 'border-box'
	},
	tableHeaderTitle: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
	},
	tableRow: {
		padding: '6px 8px',
		boxSizing: 'border-box',
		':hover': {
			'& button': {
				visibility: 'visible'
			}
		}
	},
	nameCellRoot: {
		flex: '4 1 0px',
	},
	createdAtCellRoot: {
		display: 'flex',
		justifyContent: 'center'
	},
	deleteCellRoot: {
		flex: '0.4 1 0px',
	},
	authorCellContent: {
		display: 'flex',
		alignItems: 'center',
		gap: '8px'
	},
	authorCellText: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
	},
	deleteCell: {
		visibility: 'hidden'
	},
	editCell: {
		visibility: 'hidden',
	},
	automationRuleNameDelete: {
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase300,
		fontWeight: tokens.fontWeightBold,
		lineHeight: tokens.lineHeightBase300,
	},
	createdAtText: {
		color: tokens.colorNeutralForeground2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase200,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase200,
		padding: '0 8px',
		boxSizing: 'border-box',
		textAlign: 'center',
		width: '100%',
		display: 'block'
	},
	createAutomationRuleButtonForEmptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: '16px',
		padding: '12px 0',
		marginBottom: '24px'
	}
});
